import { gql, useMutation, useQuery, useReactiveVar } from "@apollo/client";
import React from "react";
import Switch from "react-switch";
import { loggedInUser } from "src/apollo/cache";
import { PhoenixStyledTooltip } from "src/Components/Dumb/PhoenixStyledTooltip";
import { updateLocalOrg } from "src/Components/Smart/DataWrapper";
import { AppText } from "src/Components/UI";
import { PhoenixIcon } from "src/Components/UI/Phoenix";
import { info } from "src/images/NewDesign";
import { IIntegrations } from "src/types/StateTypes";
import styled from "styled-components";
import { theme } from "../../../../../utils/theme";
import { appToast } from "../../../../../utils/toast";

const UPDATE_ORG_GOOGLE_MEET_INTEGRATION = gql`
  mutation UpdateOrgGoogleMeetIntegration($googleMeetIntegration: Boolean!) {
    updateOrgGoogleMeetIntegration(google_meet_integration: $googleMeetIntegration) {
      google_meet_integration
    }
  }
`;

const CHECK_INTEGRATION_STATUS = gql`
  query checkIntegrationStatus {
    checkIntegrationStatus
  }
`;
interface CheckIntegrationStatusExpectedResponse {
  checkIntegrationStatus: {
    [key in IIntegrations]: boolean;
  };
}

const GoogleSettingsPage = () => {
  const googleMeetIntegrationEnabled = useReactiveVar(loggedInUser)?.organization?.google_meet_integration;

  const {
    data: dataStatus,
    loading: loadingStatus,
    error: errorStatus,
    refetch: refetchStatus,
  } = useQuery<CheckIntegrationStatusExpectedResponse>(CHECK_INTEGRATION_STATUS, {
    fetchPolicy: "cache-and-network",
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  const [updateOrgGoogleMeetIntegration, { loading: loadingUpdateOrgGoogleMeetIntegration }] = useMutation(
    UPDATE_ORG_GOOGLE_MEET_INTEGRATION,
    {
      onCompleted: (data) => {
        updateLocalOrg({ google_meet_integration: data?.updateOrgGoogleMeetIntegration?.google_meet_integration });
        appToast("Google integration settings updated");
      },
      onError: ({ message }) => {
        appToast(message);
      },
    },
  );

  return (
    <SettingsContainer>
      <PhoenixStyledTooltip id="google-settings-tooltip" />

      <SettingsContainerTitle>Google Meet Settings</SettingsContainerTitle>
      <ToggleRow>
        <Switch
          checked={!!googleMeetIntegrationEnabled}
          onChange={async (checked) => {
            await updateOrgGoogleMeetIntegration({
              variables: { googleMeetIntegration: checked },
            });
          }}
          onColor={theme.PRIMARY500}
          offColor={theme.NEUTRAL200}
          height={16}
          width={32}
          handleDiameter={12}
          checkedIcon={false}
          uncheckedIcon={false}
          disabled={loadingUpdateOrgGoogleMeetIntegration}
        />
        <ToggleText>Enable Google Meet Integration For Reps</ToggleText>
      </ToggleRow>
      {/* admin can't turn off calendar but we still want to show to convey info */}
      <ToggleRow>
        <Switch
          checked={!!dataStatus?.checkIntegrationStatus?.Google}
          onChange={() => {}}
          disabled={true}
          onColor={theme.PRIMARY500}
          offColor={theme.NEUTRAL200}
          height={16}
          width={32}
          handleDiameter={12}
          checkedIcon={false}
          uncheckedIcon={false}
        />
        <ToggleText>Enable Google Calendar Integration For Reps</ToggleText>
        <PhoenixIcon
          svg={info}
          size={16}
          variant="neutral"
          data-tip="This is always enabled if the Google integration is toggled on."
          data-for="google-settings-tooltip"
        />
      </ToggleRow>
    </SettingsContainer>
  );
};

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const ToggleText = styled(AppText)`
  font-size: 12px;
  font-weight: 500;
`;

const SettingsContainerTitle = styled(AppText)`
  font-size: 16px;
  font-weight: 600;
`;

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 40px;
`;

export default GoogleSettingsPage;
